import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Pagination, needsPager, useClientPage } from "@strat/ui/pagination";

import { api } from "../../api";
import { CheckList } from "../../components/Badges";
import { RefChip } from "../../components/RefChip";
import { Alert, Card, EmptyState } from "../../shell/ui";

interface Request {
  id: number;
  status: "pending" | "approved" | "rejected" | "withdrawn";
  checks: Array<{ id: string; label: string; ok: boolean; required: boolean; detail?: string }>;
  message: string;
  review_note: string;
  created_at: string;
  decided_at: string | null;
  strategy_id: string;
  name: string;
  slug: string;
  version: number;
  spec_hash: string;
  username: string;
  publisher_badge: "verified" | null;
}

/**
 * The verification queue. The machine has already checked what it can (shown);
 * the reviewer judges what it cannot: is the description honest, is it a real
 * idea rather than a copy, would we be comfortable putting our name next to it?
 */
export function VerificationTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<"pending" | "all">("pending");
  const [notes, setNotes] = useState<Record<number, string>>({});
  const list = useQuery({
    queryKey: ["admin", "badge-requests", status],
    queryFn: () => api<{ items: Request[] }>(`/hub/admin/badge-requests?status=${status}`),
  });
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: "approve" | "reject" }) =>
      api(`/hub/admin/badge-requests/${id}/${decision}`, { method: "POST", body: { note: notes[id] ?? "" } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "badge-requests"] }),
  });
  const items = list.data?.items ?? [];
  const page = useClientPage(items, { resetKey: status });
  return (
    <Card
      title={`Verification requests${status === "pending" ? ` · ${items.length} waiting` : ""}`}
      actions={
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as "pending" | "all")}
          aria-label="Which requests"
        >
          <option value="pending">Waiting</option>
          <option value="all">All, newest first</option>
        </select>
      }
    >
      <div className="stack">
        <p className="small muted">
          Approving gives that exact version a <strong>Verified</strong> badge. Rejecting needs a reason: the
          owner reads it. Both are in the audit log, and the owner is told.
        </p>
        {decide.error && <Alert tone="error">{decide.error.message}</Alert>}
        {items.length === 0 && (
          <EmptyState icon="✓" title={status === "pending" ? "Nothing waiting" : "No requests yet"}>
            Members ask from their strategy's page once the automatic checks pass.
          </EmptyState>
        )}
        {page.pageItems.map((r) => (
          <div key={r.id} className="review">
            <div className="review__head">
              <Link to={`/s/${r.strategy_id}`}>
                <strong>{r.name}</strong>
              </Link>
              <RefChip namespace={r.username} slug={r.slug} version={r.version} digest={r.spec_hash} />
              <span className="muted small">
                by @{r.username}
                {r.publisher_badge === "verified" && " (verified publisher)"} ·{" "}
                {new Date(r.created_at).toLocaleString()}
              </span>
              {r.status !== "pending" && <span className="badge">{r.status}</span>}
            </div>
            {r.message && <p className="small">“{r.message}”</p>}
            <CheckList checks={r.checks} />
            {r.status === "pending" ? (
              <div className="row review__actions">
                <input
                  placeholder="Note to the owner (required to reject)"
                  value={notes[r.id] ?? ""}
                  onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                  aria-label="Note to the owner"
                />
                <button
                  type="button"
                  className="btn btn--sm btn--primary"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: r.id, decision: "approve" })}
                >
                  ✓ Approve v{r.version}
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  disabled={decide.isPending || !(notes[r.id] ?? "").trim()}
                  onClick={() => decide.mutate({ id: r.id, decision: "reject" })}
                >
                  Reject
                </button>
              </div>
            ) : (
              r.review_note && <p className="small muted">Review: {r.review_note}</p>
            )}
          </div>
        ))}
        {needsPager(items.length) && <Pagination {...page.pager} />}
      </div>
    </Card>
  );
}
